import { SubscriptionTier } from '@workspace/database/schema';

import { keys } from '../keys';
import { getAppSumoDealGroups } from './appsumo/deal-groups';
import { tierCreditsRange } from './avatar-quality-config';
import {
  formatMinutesLimit,
  formatStorageLimit,
  formatVideoLength,
  TIER_LIMITS,
  type StripeTier
} from './limits-config';
import {
  createBillingConfig,
  FeatureGroup,
  PriceInterval,
  PriceModel,
  PriceType
} from './schema';

const currency = 'USD';

// Derived from the type x engine pricing grid so the plan-card copy can't
// drift from what billing actually charges (1-4 credits/min today).
const avatarCreditsRateNote = `${tierCreditsRange('NORMAL').min}–${tierCreditsRange('HIGH').max} credits/min by avatar type & quality`;

// Seat label derived from TIER_LIMITS.maxSeats — single source of truth with
// the enforced cap.
function seatsLabel(tier: StripeTier): string {
  const maxSeats = TIER_LIMITS[tier].maxSeats;
  return maxSeats === 1 ? '1 user' : `Up to ${maxSeats} team members`;
}

// Display-only grouped features for the subscription tier cards ("Usage / month"
// + "Limits & seats"), derived from TIER_LIMITS with the same formatters as the
// flat list so the two presentations can't drift.
function createFeatureGroupsForTier(tier: StripeTier): FeatureGroup[] {
  const limits = TIER_LIMITS[tier];
  return [
    {
      label: 'Usage / month',
      features: [
        `${formatMinutesLimit(limits.maxVideoUploadMinutes)} of video upload`,
        `${formatMinutesLimit(limits.maxVoiceOverMinutes)} of AI voice included`,
        `${limits.maxAvatarMinutesPerMonth} avatar credits (${avatarCreditsRateNote})`,
        `${limits.maxRenders} cloud render${limits.maxRenders !== 1 ? 's' : ''}`,
        'Unlimited free local renders',
        `${limits.maxTranslations} translation${limits.maxTranslations !== 1 ? 's' : ''}`
      ]
    },
    {
      label: 'Limits & seats',
      features: [
        `${formatVideoLength(limits.maxVideoLength)} max per video`,
        `${formatStorageLimit(limits.maxStorageBytes)} storage`,
        ...(limits.customAvatarUploadEnabled && limits.maxCustomAvatars > 0
          ? [
              `Train up to ${limits.maxCustomAvatars} custom AI avatar${limits.maxCustomAvatars !== 1 ? 's' : ''} (photo or digital twin)`
            ]
          : []),
        seatsLabel(tier)
      ]
    }
  ];
}

// Grouped + flat feature fields for a visible subscription tier, built from one
// source so the two presentations can't drift (the schema requires a flat
// `features`; the card renders `featureGroups` when present).
function tierCardFeatures(tier: StripeTier): {
  featureGroups: FeatureGroup[];
  features: string[];
} {
  const featureGroups = createFeatureGroupsForTier(tier);
  return {
    featureGroups,
    features: featureGroups.flatMap((group) => group.features)
  };
}

// Helper to create dynamic features list based on tier limits
function createFeaturesForTier(tier: StripeTier): string[] {
  const limits = TIER_LIMITS[tier];
  const suffix = tier === SubscriptionTier.FREE ? ' (one-time)' : ' / month';
  const features: string[] = [];

  // 1. Video upload
  features.push(
    `${formatMinutesLimit(limits.maxVideoUploadMinutes)} of video upload${suffix}`
  );

  // 2. AI voice
  features.push(
    `${formatMinutesLimit(limits.maxVoiceOverMinutes)} of AI voice included${suffix}`
  );

  // 3. Renders
  features.push(
    `${limits.maxRenders} render${limits.maxRenders !== 1 ? 's' : ''}${suffix}`
  );

  // 4. Translations
  if (limits.maxTranslations > 0) {
    features.push(
      `${limits.maxTranslations} translation${limits.maxTranslations !== 1 ? 's' : ''}${suffix}`
    );
  }

  // 5. Storage
  if (limits.maxStorageBytes > 0) {
    features.push(`${formatStorageLimit(limits.maxStorageBytes)} storage`);
  }

  // 6. Users / team members
  features.push(seatsLabel(tier));

  // 7. Max video length
  features.push(`${formatVideoLength(limits.maxVideoLength)} max / video`);

  // 8. Watermark & voice quality
  if (
    tier === SubscriptionTier.PLUS ||
    tier === SubscriptionTier.TEAM ||
    tier === SubscriptionTier.PRO
  ) {
    features.push('No watermark');
    features.push('High-quality AI voices');
  } else {
    if (limits.hasWatermark) {
      features.push('Watermark enabled');
    }
    features.push('Standard AI voice quality');
  }

  // 9. AI Avatar minutes — labeled as credits. The burn rate is avatar type x
  // quality tier (see HEYGEN_COST_PER_MIN_USD), so the card quotes the range
  // rather than a per-tier number.
  if (limits.maxAvatarMinutesPerMonth > 0) {
    features.push(
      `${limits.maxAvatarMinutesPerMonth} avatar credits / month (${avatarCreditsRateNote})`
    );
  } else {
    features.push('AI avatars (PLUS+ feature)');
  }

  // 10. Custom avatar training (photo avatars + digital twins)
  if (limits.customAvatarUploadEnabled && limits.maxCustomAvatars > 0) {
    features.push(
      `Train up to ${limits.maxCustomAvatars} custom AI avatar${limits.maxCustomAvatars !== 1 ? 's' : ''} (photo or digital twin)`
    );
  }

  return features;
}

// AppSumo lifetime-deal feature bullets — the flat projection of the grouped
// deal table so the plan card (grouped) and the activation summary / email
// (flat) can never drift from each other or from what
// applyAppSumoEntitlements grants.
export function createAppSumoFeatures(
  appsumoTier: number | null | undefined
): string[] {
  return Object.values(getAppSumoDealGroups(appsumoTier)).flat();
}

export const billingConfig = createBillingConfig({
  products: [
    {
      id: 'free',
      name: 'Free',
      description:
        'Perfect for trying out VideoMule with basic features and limited usage.',
      label: 'Get started',
      isFree: true,
      hidden: true, // Hidden - use Trial instead
      features: createFeaturesForTier(SubscriptionTier.FREE),
      // Even if it is free, keep the plans and prices to display the interval and currency correctly
      plans: [
        {
          id: 'plan-free-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price-free-month-id', // a placebo ID is fine here
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 0,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'trial',
      name: 'Trial',
      description: 'Try VideoMule with basic features at a minimal cost.',
      label: 'Start trial',
      // DO NOT REMOVE this product entry or NEXT_PUBLIC_BILLING_PRICE_TRIAL_MONTH_ID below —
      // grandfathered $0.99 subscriptions still map their price→tier through it. Without this
      // entry, getTierFromPriceId falls through to FREE, which would downgrade every
      // grandfathered org on their next renewal webhook.
      hidden: true,
      features: createFeaturesForTier(SubscriptionTier.TRIAL),
      plans: [
        {
          id: 'plan-trial-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_TRIAL_MONTH_ID ||
                'price-trial-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 0.99,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'plus',
      name: 'Creator',
      headline: 'For solo creators who want studio-quality video',
      description:
        'Perfect for solo creators who want high-quality voices, more uploads, and watermark-free videos.',
      label: 'Get started',
      recommended: true,
      couponId: keys().NEXT_PUBLIC_BILLING_COUPON_PLUS,
      launchOffer: 'Launch offer - 6 months discount',
      ...tierCardFeatures(SubscriptionTier.PLUS),
      plans: [
        {
          id: 'plan-plus-month',
          displayIntervals: [PriceInterval.Month],
          trialDays: 7,
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PLUS_MONTH_ID ||
                'price-plus-month-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 29,
              originalCost: 49,
              currency
            }
          ]
        },
        {
          id: 'plan-plus-year',
          displayIntervals: [PriceInterval.Year],
          trialDays: 7, // mirrors the monthly plan's Stripe-native trial
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PLUS_YEAR_ID ||
                'price-plus-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 228, // annual billed at $19/mo (below the $29 monthly list)
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'team',
      name: 'Team',
      headline: 'For small teams producing video at volume, together',
      description:
        'Ideal for small teams collaborating on video content with shared storage and up to 5 users.',
      label: 'Get started',
      couponId: keys().NEXT_PUBLIC_BILLING_COUPON_TEAM,
      recommended: false,
      launchOffer: 'Launch offer - 6 months discount',
      ...tierCardFeatures(SubscriptionTier.TEAM),
      plans: [
        {
          id: 'plan-team-month',
          displayIntervals: [PriceInterval.Month],
          trialDays: 7,
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_TEAM_MONTH_ID ||
                'price-team-month-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 69,
              originalCost: 99,
              currency
            }
          ]
        },
        {
          id: 'plan-team-year',
          displayIntervals: [PriceInterval.Year],
          trialDays: 7, // mirrors the monthly plan's Stripe-native trial
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_TEAM_YEAR_ID ||
                'price-team-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 588, // annual billed at $49/mo (below the $69 monthly list)
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'pro',
      name: 'Agency',
      headline: 'For agencies delivering client video at scale',
      description:
        'Built for agencies and enterprises with high-volume needs, longer videos, and up to 15 team members.',
      label: 'Get started',
      couponId: keys().NEXT_PUBLIC_BILLING_COUPON_PRO,
      ...tierCardFeatures(SubscriptionTier.PRO),
      plans: [
        {
          id: 'plan-pro-month',
          displayIntervals: [PriceInterval.Month],
          trialDays: 7,
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_MONTH_ID ||
                'price-pro-month-id', // keep for marketing pages, so you only need to specify id in dashboard
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 199,
              currency
            }
          ]
        },
        {
          id: 'plan-pro-year',
          displayIntervals: [PriceInterval.Year],
          trialDays: 7, // mirrors the monthly plan's Stripe-native trial
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_YEAR_ID ||
                'price-pro-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 1908, // annual billed at $159/mo (below the $199 monthly list)
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'credit-pack-starter',
      name: 'Starter Pack',
      description:
        'A starter bundle of upload minutes, voiceovers, renders, and more.',
      label: 'Buy now',
      hidden: true,
      features: [
        '30 mins of video upload (credit pack)',
        '50 mins of AI voice included (credit pack)',
        '30 cloud renders (credit pack)',
        'Unlimited free local renders while your pack is active',
        '15 translations (credit pack)',
        '10 GB storage',
        'No watermark',
        'High-quality AI voices',
        '5 AI avatar minutes'
      ],
      plans: [
        {
          id: 'plan-credit-pack-starter',
          displayIntervals: [PriceInterval.OneTime],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_CREDIT_PACK_STARTER_ID ||
                'price-credit-pack-starter-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 79,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'credit-pack-standard',
      name: 'Standard Pack',
      description:
        'A standard bundle of upload minutes, voiceovers, renders, and more.',
      label: 'Buy now',
      features: [
        '100 mins of video upload (credit pack)',
        '180 mins of AI voice included (credit pack)',
        '100 cloud renders (credit pack)',
        'Unlimited free local renders while your pack is active',
        '50 translations (credit pack)',
        '30 GB storage',
        'No watermark',
        'High-quality AI voices',
        '15 AI avatar minutes'
      ],
      plans: [
        {
          id: 'plan-credit-pack-standard',
          displayIntervals: [PriceInterval.OneTime],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_CREDIT_PACK_STANDARD_ID ||
                'price-credit-pack-standard-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 199,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'credit-pack-power',
      name: 'Power Pack',
      description:
        'A high-volume bundle of upload minutes, voiceovers, renders, and more.',
      label: 'Buy now',
      hidden: true,
      features: [
        '250 mins of video upload (credit pack)',
        '450 mins of AI voice included (credit pack)',
        '250 cloud renders (credit pack)',
        'Unlimited free local renders while your pack is active',
        '125 translations (credit pack)',
        '75 GB storage',
        'No watermark',
        'High-quality AI voices',
        '25 AI avatar minutes'
      ],
      plans: [
        {
          id: 'plan-credit-pack-power',
          displayIntervals: [PriceInterval.OneTime],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_CREDIT_PACK_POWER_ID ||
                'price-credit-pack-power-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 449,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-upload',
      name: 'Upload Boost',
      description: 'Add 30 extra upload minutes to your current billing cycle.',
      label: 'Buy now',
      features: ['+30 mins of video upload (this cycle)'],
      plans: [
        {
          id: 'plan-addon-upload',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_UPLOAD_ID ||
                'price-addon-upload-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 10,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-voice',
      name: 'Voiceover Boost',
      description:
        'Add 30 extra voiceover minutes to your current billing cycle.',
      label: 'Buy now',
      features: ['+30 mins of AI voice (this cycle)'],
      plans: [
        {
          id: 'plan-addon-voice',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_VOICE_ID ||
                'price-addon-voice-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 15,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-render',
      name: 'Render Pack',
      description: 'Add 30 extra cloud renders to your current billing cycle.',
      label: 'Buy now',
      features: ['+30 cloud renders (this cycle)'],
      plans: [
        {
          id: 'plan-addon-render',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_RENDER_ID ||
                'price-addon-render-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 10,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-translation',
      name: 'Translation Pack',
      description: 'Add 30 extra translations to your current billing cycle.',
      label: 'Buy now',
      features: ['+30 translations (this cycle)'],
      plans: [
        {
          id: 'plan-addon-translation',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_TRANSLATION_ID ||
                'price-addon-translation-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 10,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-storage',
      name: 'Storage Boost',
      description: 'Add 10 GB of extra storage, billed monthly.',
      hidden: true, // TODO: requires subscription-addon lifecycle handling before enabling
      label: 'Buy now',
      features: ['+10 GB storage (monthly)'],
      plans: [
        {
          id: 'plan-addon-storage',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_STORAGE_ID ||
                'price-addon-storage-id',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 5,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-avatar',
      name: 'Avatar Minutes Pack',
      description:
        'Add 10 extra minutes of AI avatar video to your current billing cycle.',
      label: 'Buy now',
      features: ['+10 avatar minutes (this cycle)'],
      plans: [
        {
          id: 'plan-addon-avatar',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_AVATAR_ID ||
                'price-addon-avatar-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 20,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'addon-custom-avatar',
      name: 'Custom Avatar Slot',
      description: 'Add 1 permanent custom avatar slot to your organization.',
      label: 'Buy now',
      features: ['+1 custom avatar slot'],
      plans: [
        {
          id: 'plan-addon-custom-avatar',
          displayIntervals: [PriceInterval.AddOns],
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_ADDON_CUSTOM_AVATAR_ID ||
                'price-addon-custom-avatar-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 9,
              currency
            }
          ]
        }
      ]
    },
    {
      // Lifetime-deal plan source. Hidden everywhere a price is shown — it is
      // never purchasable through Stripe. The entry exists so
      // getTierDisplayName('appsumo') labels every tier badge, and so the
      // plan card has a product name to render.
      id: 'appsumo',
      // Short on purpose: getTierDisplayName('appsumo') labels every badge from
      // this, and the long "AppSumo Tier N · Plan" form comes from
      // getAppSumoPlanLabel.
      name: 'AppSumo',
      description:
        'Lifetime access purchased on AppSumo. Billing and plan changes are managed from your AppSumo account.',
      label: 'Manage on AppSumo',
      hidden: true,
      features: createAppSumoFeatures(null),
      // The schema requires at least one plan/price; AppSumo bills the buyer,
      // so this price is a placebo that no Stripe object ever references.
      plans: [
        {
          id: 'plan-appsumo-lifetime',
          displayIntervals: [PriceInterval.OneTime],
          prices: [
            {
              id: 'price-appsumo-lifetime-id',
              type: PriceType.OneTime,
              model: PriceModel.Flat,
              cost: 0,
              currency
            }
          ]
        }
      ]
    }
  ]
});

export const billingConfigDisplayIntervals = Array.from(
  new Set(
    billingConfig.products
      .filter((product) => !product.hidden)
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.displayIntervals)
      )
      .filter((interval) => interval && interval !== PriceInterval.AddOns)
  )
) as PriceInterval[];
